import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import {
  ApiError,
  addIntegration,
  createPark,
  deleteIntegration,
  fetchConnectors,
  fetchExternalUnits,
  fetchPark,
  fetchParks,
  patchIntegration,
  patchPark,
  patchUnit,
  putMappings,
  errorMessage,
  syncIntegration,
  uploadPlan,
} from './api'
import type {
  AdminUnit,
  ConnectorInfo,
  ExternalUnit,
  Integration,
  MappingInput,
  MappingResult,
  ParkDetail,
  ParkInput,
  ParkPatch,
  ParkSummary,
  SettingValue,
  SyncResult,
  UnitPatch,
  PlanUploadResult,
} from './types'

export const parksKey = ['parks']
export const connectorsKey = ['connectors']
export const parkKey = (slug: string) => ['park', slug]
export const externalUnitsKey = (integrationId: string) => ['external-units', integrationId]

export function useParks() {
  return useQuery<ParkSummary[], ApiError>({ queryKey: parksKey, queryFn: fetchParks })
}

export function usePark(slug: string | null) {
  return useQuery<ParkDetail, ApiError>({
    queryKey: parkKey(slug ?? ''),
    queryFn: () => fetchPark(slug as string),
    enabled: slug !== null,
  })
}

export function useCreatePark() {
  const client = useQueryClient()
  return useMutation<ParkSummary, ApiError, ParkInput>({
    mutationFn: (park) => createPark(park),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: parksKey })
    },
  })
}

export function useConnectors() {
  return useQuery<ConnectorInfo[], ApiError>({ queryKey: connectorsKey, queryFn: fetchConnectors, staleTime: 300_000 })
}

/**
 * Asking a booking system for its accommodations is a live call and can take seconds, so the answer is
 * kept for the whole session and only refetched when someone presses "Types ophalen".
 */
export function useExternalUnits(integrationId: string, enabled: boolean) {
  return useQuery<{ units: ExternalUnit[] }, ApiError>({
    queryKey: externalUnitsKey(integrationId),
    queryFn: () => fetchExternalUnits(integrationId),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: 0,
  })
}

function writeUnit(client: QueryClient, slug: string, unit: AdminUnit) {
  client.setQueryData<ParkDetail>(parkKey(slug), (current) => {
    if (!current) return current
    return {
      ...current,
      units: current.units.map((u) => (u.id === unit.id ? { ...u, ...unit } : u)),
    }
  })
}

/** Een nieuwe plattegrond verandert de afmetingen en daarmee de hele kaart, dus alles opnieuw ophalen. */
export function useUploadPlan(slug: string) {
  const client = useQueryClient()
  return useMutation<PlanUploadResult, ApiError, File>({
    mutationFn: (file) => uploadPlan(slug, file),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: parkKey(slug) })
      void client.invalidateQueries({ queryKey: parksKey })
    },
  })
}

export function useUpdatePark(slug: string) {
  const client = useQueryClient()
  return useMutation<unknown, ApiError, ParkPatch>({
    mutationFn: (patch) => patchPark(slug, patch),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: parkKey(slug) })
      void client.invalidateQueries({ queryKey: parksKey })
    },
  })
}

export function useUpdateUnit(slug: string) {
  const client = useQueryClient()
  return useMutation<AdminUnit, ApiError, { id: string; patch: UnitPatch }>({
    mutationFn: ({ id, patch }) => patchUnit(id, patch),
    onSuccess: (unit, { id, patch }) => {
      // The API answers with the stored unit; fall back to what we sent if it answers something else,
      // so the row never shows stale values after a save.
      const merged: AdminUnit = unit && typeof unit === 'object' && 'id' in unit ? unit : ({ id, ...patch } as AdminUnit)
      writeUnit(client, slug, merged)
    },
  })
}

/** PUT /mappings upserts the rows in the body and deletes the ones whose ids are both null. */
export function useSaveMappings(slug: string) {
  const client = useQueryClient()
  return useMutation<MappingResult, ApiError, { integrationId: string; mappings: MappingInput[] }>({
    mutationFn: ({ integrationId, mappings }) => putMappings(integrationId, mappings),
    onSuccess: (_result, { integrationId, mappings }) => {
      const byNumber = new Map(mappings.map((m) => [m.unitNumber, m]))
      client.setQueryData<ParkDetail>(parkKey(slug), (current) => {
        if (!current) return current
        return {
          ...current,
          units: current.units.map((unit) => {
            const next = byNumber.get(unit.number)
            if (!next) return unit // not in the body, so the API left it alone
            const updated = { ...unit.mappings }
            if (!next.externalUnitId && !next.externalTypeId) delete updated[integrationId]
            else updated[integrationId] = { externalUnitId: next.externalUnitId, externalTypeId: next.externalTypeId }
            return { ...unit, mappings: updated }
          }),
        }
      })
      void client.invalidateQueries({ queryKey: parksKey })
    },
  })
}

export function useIntegrationSettings(slug: string) {
  const client = useQueryClient()
  return useMutation<Integration, ApiError, { id: string; settings?: Record<string, SettingValue>; clearSettings?: string[]; enabled?: boolean }>({
    mutationFn: ({ id, settings, clearSettings, enabled }) => patchIntegration(id, { settings, clearSettings, enabled }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: parkKey(slug) })
      void client.invalidateQueries({ queryKey: parksKey })
    },
  })
}

export function useAddIntegration(slug: string) {
  const client = useQueryClient()
  return useMutation<Integration, ApiError, string>({
    mutationFn: (connector) => addIntegration(slug, connector),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: parkKey(slug) })
      void client.invalidateQueries({ queryKey: parksKey })
    },
  })
}

export function useRemoveIntegration(slug: string) {
  const client = useQueryClient()
  return useMutation<unknown, ApiError, string>({
    mutationFn: (id) => deleteIntegration(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: parkKey(slug) })
      void client.invalidateQueries({ queryKey: parksKey })
    },
  })
}

export function useSyncIntegration(slug: string) {
  const client = useQueryClient()
  return useMutation<SyncResult, ApiError, { id: string; days?: number }>({
    mutationFn: ({ id, days }) => syncIntegration(id, days),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: parkKey(slug) })
      void client.invalidateQueries({ queryKey: parksKey })
    },
  })
}

export interface ExternalUnitsState {
  units: ExternalUnit[]
  loading: boolean
  /** A real problem worth showing in red. */
  error: string | null
  /** 422: this connector simply cannot list accommodations (no API token). Not a failure. */
  hint: string | null
  refetch: () => void
}

/**
 * The same live call as useExternalUnits, but for a list of integrations that can grow or shrink,
 * so the Plaatsen table can fill one select per koppeling without breaking the rules of hooks.
 */
export function useExternalUnitsMap(integrationIds: string[]): Map<string, ExternalUnitsState> {
  const results = useQueries({
    queries: integrationIds.map((id) => ({
      queryKey: externalUnitsKey(id),
      queryFn: () => fetchExternalUnits(id),
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
      retry: 0,
    })),
  })

  const map = new Map<string, ExternalUnitsState>()
  integrationIds.forEach((id, index) => {
    const result = results[index]
    const failed = result?.isError ? result.error : null
    const cannotList = failed instanceof ApiError && failed.status === 422
    map.set(id, {
      units: result?.data?.units ?? [],
      loading: result?.isPending ?? false,
      error: failed && !cannotList ? errorMessage(failed) : null,
      hint: cannotList ? errorMessage(failed) : null,
      refetch: () => {
        void result?.refetch()
      },
    })
  })
  return map
}
